import React, { useMemo, useRef } from 'react';
import { Cluster } from '../../../utils/clustering';
import { NetworkConnection } from '../../../utils/teamShapeNetwork';
import { transformPoints, RotationAngle } from '../utils';

interface NetworkLinesProps {
  clusters: Cluster[];
  connections: NetworkConnection[];
  rotation?: RotationAngle;
  scale?: number;
  lineColor?: string;
  minThickness?: number; // Minimum line thickness
  maxThickness?: number; // Maximum line thickness
  opacity?: number; // Line opacity
}

/**
 * Network Lines visualization component
 * Renders connection arrows between player clusters
 * Arrow thickness represents connection strength (number of events)
 * Arrow direction shows actual flow:
 *   - FROM: Source point of event (e.g., passer's position)
 *   - TO: Destination point of event (e.g., receiver's position)
 * 
 * For passes: Arrow points FROM passer TO receiver
 * For dribbles: Arrow points FROM start position TO end position
 * For crosses/corners: Arrow points FROM origin TO target
 */
const NetworkLines: React.FC<NetworkLinesProps> = ({
  clusters,
  connections,
  rotation = 0,
  scale = 1.0,
  lineColor = '#3B82F6',
  minThickness = 1,
  maxThickness = 6,
  opacity = 0.6,
}) => {
  // Calculate thickness range for normalization
  const thicknessRange = useMemo(() => {
    if (connections.length === 0) {
      return { min: 0, max: 0 };
    }

    const strengths = connections.map(c => c.strength);
    const min = Math.min(...strengths);
    const max = Math.max(...strengths);

    return { min, max };
  }, [connections]);

  // Normalize strength to line thickness using power scaling for better visual distinction
  const getLineThickness = (strength: number): number => {
    if (thicknessRange.max === thicknessRange.min) {
      return (minThickness + maxThickness) / 2;
    }

    // Use power scaling (0.5) to make differences more visible
    const normalized = (strength - thicknessRange.min) / (thicknessRange.max - thicknessRange.min);
    const powerNormalized = Math.pow(normalized, 0.5);
    return minThickness + powerNormalized * (maxThickness - minThickness);
  };

  // Generate unique marker ID to avoid conflicts (using ref for stable ID)
  // Must be called before any early returns (React Hooks rule)
  const markerIdRef = useRef(`arrowhead-${lineColor.replace('#', '')}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const markerId = markerIdRef.current;

  // Transform cluster centroids based on rotation
  const transformedClusters = useMemo(() => {
    return clusters.map(cluster => {
      const transformed = transformPoints([cluster.centroid], rotation, scale);
      return {
        ...cluster,
        transformedCentroid: transformed[0]
      };
    });
  }, [clusters, rotation, scale]);

  if (connections.length === 0 || clusters.length === 0) {
    return null;
  }

  return (
    <g id="network-lines">
      <defs>
        {/* Arrow marker definition */}
        <marker
          id={markerId}
          markerWidth="10"
          markerHeight="10"
          refX="9"
          refY="3"
          orient="auto"
        >
          <polygon
            points="0 0, 10 3, 0 6"
            fill={lineColor}
            opacity={opacity}
          />
        </marker>
      </defs>
      {connections.map((connection, index) => {
        const fromCluster = transformedClusters[connection.fromClusterIndex];
        const toCluster = transformedClusters[connection.toClusterIndex];

        if (!fromCluster || !toCluster) return null;

        const thickness = getLineThickness(connection.strength);
        const { x: x1, y: y1 } = fromCluster.transformedCentroid;
        const { x: x2, y: y2 } = toCluster.transformedCentroid;

        // Calculate distance and adjust arrow endpoint to avoid overlapping with cluster circle
        // Estimate cluster radius (we'll use a conservative estimate based on maxSize)
        const estimatedRadius = 20; // Approximate radius of largest cluster circle
        const dx = x2 - x1;
        const dy = y2 - y1;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        // Adjust endpoint to stop before the cluster circle
        const adjustedDistance = Math.max(0, distance - estimatedRadius);
        const adjustedX2 = x1 + (dx / distance) * adjustedDistance;
        const adjustedY2 = y1 + (dy / distance) * adjustedDistance;

        return (
          <line
            key={`connection-${index}`}
            x1={x1}
            y1={y1}
            x2={adjustedX2}
            y2={adjustedY2}
            stroke={lineColor}
            strokeWidth={thickness}
            opacity={opacity}
            markerEnd={`url(#${markerId})`}
            className="network-connection"
          />
        );
      })}
    </g>
  );
};

export default NetworkLines;

